// ============================================================
// 卡牌系統 — 出牌轉換 + 單機 AI 排程（M3）
// ============================================================
// 兩件事：
//   1. cardPlaysToSchedule(plays) — 把玩家排好的 CardPlay[] 依
//      (tick, 目標, 元素) 分組成 SpellSchedule（3 筆 SpellCommand）。
//      同 tick + 同目標 + 同元素的 K 張 → 一筆 cast 帶 stackCount=K。
//      friendly / hostile 由 spell.ts 依 SPELLS[el].phase 自動路由 tick
//      內子階段，這裡不分。
//   2. planAiSchedule(...) — 保守貪婪 AI：每 tick 對手牌算最佳目標，
//      達「高收益門檻」才出，否則 Pass（留牌）；只在逼近手牌上限才強制。
//      濾牌牌（抽/搜）規劃階段即時結算、不佔 tick。
//
// 對接設計文件 §3 / 規格書 §4–§5；單張基礎值校準在 balance.ts §14。
// ============================================================

import { md } from '../utils.ts';
import { ELEM, SPELLS, UCFG, CARD_EFFECT_FIXED_POWER } from '../constants.ts';
import type { BattleUnit, Base, Pos } from '../BattleUnit.ts';
import { getTerrainAt, type TerrainGrid } from '../server/terrain.ts';
import type { SpellCommand, SpellSchedule } from '../server/types.ts';
import { CARDS } from './CardData.ts';
import type { CardDef, CardId, CardInstance, CardOp, CardPlay, ElementId } from './CardTypes.ts';
import type { Deck } from './Deck.ts';

// ---------------------------------------------------------------
// 元素 → 卡牌定義（用於判斷疊加 / 效果分類）
// ---------------------------------------------------------------

/** element id → CardDef（只含 stackable / effect；filter 卡無 element）。 */
const ELEMENT_TO_CARD = new Map<ElementId, CardDef>();
for (const id of Object.keys(CARDS)) {
  const def = CARDS[id];
  if (def.element !== undefined) ELEMENT_TO_CARD.set(def.element, def);
}

/** 該元素是否為可疊加牌（火/閃/補/攻/盾）。effect 牌回 false。 */
function isStackableElement(el: ElementId): boolean {
  return ELEMENT_TO_CARD.get(el)?.category === 'stackable';
}

const passCmd = (tick: number): SpellCommand => ({ slotIndex: tick, spellType: 0, powerLevel: 0 });

/**
 * 組一筆 cast。疊加牌帶 stackCount（powerLevel 設 1、被 spell.ts 忽略）；
 * 效果牌不帶 stackCount、走 CARD_EFFECT_FIXED_POWER 的 powerLevel 路徑。
 */
export function buildCast(
  tick: number,
  element: ElementId,
  target: Pos,
  copies: number,
): SpellCommand {
  if (isStackableElement(element)) {
    return {
      slotIndex: tick,
      spellType: element,
      powerLevel: 1,
      stackCount: Math.max(1, copies),
      targetPos: { x: target.x, y: target.y },
    };
  }
  // 效果牌：固定值、無等級、無疊加。
  return {
    slotIndex: tick,
    spellType: element,
    powerLevel: CARD_EFFECT_FIXED_POWER,
    targetPos: { x: target.x, y: target.y },
  };
}

/**
 * CardPlay[] → SpellSchedule。每個 tick 收斂成一筆 cast（規格 §4：每 tick
 * 一道法術），疊加張數 = 該 tick 同元素的牌數。空 tick = Pass。
 *
 * 約定：UI 保證同一 tick 內所有牌同元素同目標（M4 拖卡互動會強制）；
 * 防呆下這裡以該 tick 第一張牌的元素 / 目標為準，並只計同元素張數。
 */
export function cardPlaysToSchedule(plays: ReadonlyArray<CardPlay>): SpellSchedule {
  const schedule: SpellSchedule = [passCmd(0), passCmd(1), passCmd(2)];
  for (let tick = 0; tick < 3; tick++) {
    const inTick = plays.filter((p) => p.tick === tick && p.target);
    if (inTick.length === 0) continue;
    const first = inTick[0];
    const def = CARDS[first.cardId];
    if (!def || def.element === undefined) continue; // filter 牌不該落 tick
    const element = def.element;
    const copies = inTick.filter((p) => CARDS[p.cardId]?.element === element).length;
    schedule[tick] = buildCast(tick, element, first.target as Pos, copies);
  }
  return schedule;
}

// ---------------------------------------------------------------
// PvP 反作弊 — cast schedule 對「權威手牌」的持有驗證（M6）
// ---------------------------------------------------------------
// 元素 ↔ cardId 在 13 張目錄是 1:1（每張 stackable/effect 牌一個獨佔元素），
// 所以一份 cast schedule 要消耗的牌，可用「各元素張數」唯一表示，server 端
// 即可拿自己的權威手牌計數比對，杜絕 client 謊報持有。filter 牌不產生 cast
// （它們走 cardOp 即時結算），故不在此驗。

/**
 * 算一份 schedule 各元素要消耗幾張牌。疊加牌吃 `stackCount`；效果牌每筆 1 張
 * （效果牌不帶 stackCount → `?? 1`）。Pass（spellType 0）略過。
 */
export function ownershipNeed(schedule: ReadonlyArray<SpellCommand>): Map<ElementId, number> {
  const need = new Map<ElementId, number>();
  for (const cmd of schedule) {
    if (!cmd || cmd.spellType === 0) continue;
    const el = cmd.spellType;
    need.set(el, (need.get(el) ?? 0) + Math.max(1, cmd.stackCount ?? 1));
  }
  return need;
}

/** 把一手牌依元素計數（filter 牌無 element，略過）。 */
export function handElementCounts(hand: ReadonlyArray<CardInstance>): Map<ElementId, number> {
  const counts = new Map<ElementId, number>();
  for (const c of hand) {
    const el = CARDS[c.cardId]?.element;
    if (el === undefined) continue;
    counts.set(el, (counts.get(el) ?? 0) + 1);
  }
  return counts;
}

/**
 * 驗證一份 cast schedule 是否被該隊「當前權威手牌」涵蓋（PvP 反作弊核心）。
 * 結構合法性（slot 數 / stackCount 正整數 / 目標邊界）仍由 server/validation.ts
 * 的 `validateCardSchedule` 把關；本函式只負責「手上是否真有這些牌」。
 */
export function validateCastsAgainstHand(
  schedule: ReadonlyArray<SpellCommand>,
  hand: ReadonlyArray<CardInstance>,
): { valid: boolean; reason?: string } {
  const need = ownershipNeed(schedule);
  const have = handElementCounts(hand);
  for (const [el, n] of need) {
    const h = have.get(el) ?? 0;
    if (n > h) {
      return { valid: false, reason: `element ${el} needs ${n} card(s) but hand holds ${h}` };
    }
  }
  return { valid: true };
}

/**
 * 出牌結算：把一份 cast schedule 消耗的牌從 deck.hand 移到 discardPile（server 權威
 * deck 同步用，讓下回合 ownership 計數正確）。必須在 `validateCastsAgainstHand` 通過
 * 後呼叫。元素 ↔ cardId 1:1，按各元素消耗張數丟對應卡。discardPile 寫入順序無妨
 * （`Deck.reshuffle` 會 canonical 排序）。
 */
export function discardCastsFromDeck(deck: Deck, schedule: ReadonlyArray<SpellCommand>): void {
  const need = ownershipNeed(schedule);
  for (const [el, n] of need) {
    const def = ELEMENT_TO_CARD.get(el);
    if (!def) continue;
    for (let i = 0; i < n; i++) deck.discardOneByCardId(def.id);
  }
}

/**
 * 把一個濾牌 `CardOp` 套用到 deck（**client 本地與 server 權威共用的單一路徑**）。
 *
 * 為什麼要共用：濾牌操作會把牌丟進 discardPile，而 reshuffle 會對 drawPile+discardPile
 * 整體做 seeded Fisher–Yates。若兩端套用順序不同 → discardPile 順序不同 → 洗牌後
 * drawPile 不同 → PvP 雙端 deck desync。讓兩端都呼叫本函式，保證 byte-identical。
 *
 * 數量一律由目錄 `filterAmount` 推導（不信任 wire 帶的數字）。先驗後改：任一前置
 * 條件不符（spent 不在手 / cycle 棄牌不在手 / tutor want 不在抽牌堆 / 種類不符）
 * 即 reject 且不 mutate deck。
 */
export function applyCardOp(deck: Deck, op: CardOp): { ok: boolean; reason?: string } {
  const def = CARDS[op.spent];
  if (!def || def.category !== 'filter') {
    return { ok: false, reason: `cardOp: '${op.spent}' is not a filter card` };
  }

  // ---- 驗證（不 mutate）：以手牌計數模擬「拿走」 ----
  const handCounts = new Map<CardId, number>();
  for (const c of deck.hand) handCounts.set(c.cardId, (handCounts.get(c.cardId) ?? 0) + 1);
  const take = (id: CardId): boolean => {
    const n = handCounts.get(id) ?? 0;
    if (n <= 0) return false;
    handCounts.set(id, n - 1);
    return true;
  };
  if (!take(op.spent)) return { ok: false, reason: `cardOp: spent '${op.spent}' not in hand` };

  if (op.kind === 'draw') {
    if (def.filterKind !== 'draw') return { ok: false, reason: `cardOp draw: '${op.spent}' is not a draw card` };
  } else if (op.kind === 'tutor') {
    if (def.filterKind !== 'tutor') return { ok: false, reason: `cardOp tutor: '${op.spent}' is not a tutor card` };
    if (!deck.drawPile.some((c) => c.cardId === op.want)) {
      return { ok: false, reason: `cardOp tutor: '${op.want}' not in draw pile` };
    }
  } else { // cycle
    if (def.filterKind !== 'cycle') return { ok: false, reason: `cardOp cycle: '${op.spent}' is not a cycle card` };
    const cap = def.filterAmount ?? 2;
    if (op.discard.length > cap) {
      return { ok: false, reason: `cardOp cycle: discard ${op.discard.length} exceeds cap ${cap}` };
    }
    for (const d of op.discard) {
      if (!take(d)) return { ok: false, reason: `cardOp cycle: '${d}' not available in hand` };
    }
  }

  // ---- 套用（mutate）：順序在兩端必須一致 ----
  deck.discardOneByCardId(op.spent);
  if (op.kind === 'draw') {
    deck.draw(Math.max(0, def.filterAmount ?? 2));
  } else if (op.kind === 'tutor') {
    deck.tutor(op.want);
  } else { // cycle
    for (const d of op.discard) deck.discardOneByCardId(d);
    deck.draw(op.discard.length);
  }
  return { ok: true };
}

// ---------------------------------------------------------------
// 保守貪婪 AI 排程
// ---------------------------------------------------------------

/** 朝敵方施放的元素（傷害 / 控場）。 */
const ENEMY_AIM = new Set<ElementId>([1 /*火*/, 5 /*閃*/, 10 /*落石*/, 8 /*長草*/, 9 /*降雨*/, 7 /*旋風敵*/]);
/** 朝我方施放的元素（治療 / 增益）。 */
const ALLY_AIM = new Set<ElementId>([2 /*補血*/, 3 /*護盾*/, 4 /*加攻*/, 6 /*旋風友*/]);

interface Candidate {
  element: ElementId;
  target: Pos;
  score: number;
}

function maxSoldiers(u: BattleUnit): number {
  const cfg = UCFG[u.type];
  return cfg ? cfg.hp : u.totalSoldiers;
}

function radiusOf(el: ElementId): number {
  const sp = SPELLS[ELEM[el]];
  return sp ? sp.radius : 1;
}

/** 評估一個元素在當前盤面的最佳目標 + 分數；達不到門檻回 null（保守：寧可留牌）。 */
function evaluateElement(
  el: ElementId,
  units: BattleUnit[],
  terrain: TerrainGrid | null | undefined,
  aiTeam: number,
): Candidate | null {
  const enemies = units.filter((u) => !u.dead && u.team !== aiTeam);
  const allies = units.filter((u) => !u.dead && u.team === aiTeam);
  const r = radiusOf(el);

  if (ENEMY_AIM.has(el)) {
    if (enemies.length === 0) return null;
    let best: Candidate | null = null;
    for (const c of enemies) {
      const inR = enemies.filter((u) => md(u.pos, c.pos) <= r);
      const count = inR.length;
      let score = inR.reduce((s, u) => s + u.totalSoldiers, 0);
      // 地形加成：火球→敵踩草（點燃）、閃電→敵踩水、降雨→敵踩水（淹）。
      if (terrain) {
        for (const u of inR) {
          const cell = getTerrainAt(terrain, u.pos.x, u.pos.y);
          if (el === 1 && cell === 'grass') score += 6;
          if ((el === 5 || el === 9) && cell === 'puddle') score += 8;
          if (el === 8 && cell === 'grass') score += 6; // 長草 root
        }
      }
      // 門檻（保守：高收益才出）
      let meets: boolean;
      if (el === 10) {
        // 落石 radius 0：砸中強單位（或可擊殺）才值得
        meets = c.totalSoldiers >= 20;
      } else if (el === 9) {
        // 降雨：敵踩水（淹）或同時打到 2 個以上才出
        const anyPuddle = terrain
          ? inR.some((u) => getTerrainAt(terrain, u.pos.x, u.pos.y) === 'puddle')
          : false;
        meets = anyPuddle || count >= 2;
      } else if (el === 8) {
        const anyGrass = terrain
          ? inR.some((u) => getTerrainAt(terrain, u.pos.x, u.pos.y) === 'grass')
          : false;
        meets = anyGrass || count >= 2;
      } else {
        // 火 / 閃 / 旋風敵：至少命中 2 個敵單位
        meets = count >= 2;
      }
      if (!meets) continue;
      if (!best || score > best.score) best = { element: el, target: { ...c.pos }, score };
    }
    return best;
  }

  if (ALLY_AIM.has(el)) {
    if (allies.length === 0) return null;
    let best: Candidate | null = null;
    for (const c of allies) {
      const inR = allies.filter((u) => md(u.pos, c.pos) <= r);
      if (el === 2 || el === 6) {
        // 補血 / 旋風友：挑能覆蓋最多殘血(<60%)友軍的格
        const wounded = inR.filter((u) => u.totalSoldiers < maxSoldiers(u) * 0.6);
        if (wounded.length < 1) continue;
        const score = wounded.reduce((s, u) => s + (maxSoldiers(u) - u.totalSoldiers), 0);
        if (!best || score > best.score) best = { element: el, target: { ...c.pos }, score };
      } else {
        // 護盾 / 加攻：覆蓋最多前排友軍（離敵 ≤ 3）；至少 2 個才出
        const front = inR.filter((u) =>
          enemies.some((e) => md(u.pos, e.pos) <= 3),
        );
        if (front.length < 2) continue;
        const score = front.length * 10 + inR.length;
        if (!best || score > best.score) best = { element: el, target: { ...c.pos }, score };
      }
    }
    return best;
  }

  return null;
}

/**
 * 規劃階段即時結算 AI 的濾牌牌（不佔 tick）：
 *   - 抽牌：棄掉該牌、抽 filterAmount 張。
 *   - 搜牌：把抽牌堆第一張「可疊加牌」搜上手（湊疊加），棄掉搜牌本身。
 *   - 調度：M3 暫不處理（留在手上，行為無害）。
 * 直接 mutate deck.hand / drawPile / discardPile。
 */
function resolveAiFilters(deck: Deck): void {
  // snapshot uid 以免邊改邊讀
  const filterUids = deck.hand
    .filter((c) => CARDS[c.cardId]?.category === 'filter')
    .map((c) => ({ uid: c.uid, cardId: c.cardId }));
  for (const { uid, cardId } of filterUids) {
    const def = CARDS[cardId];
    if (!def) continue;
    if (def.filterKind === 'draw') {
      deck.discardFromHand(uid);
      deck.draw(def.filterAmount ?? 2);
    } else if (def.filterKind === 'tutor') {
      // 找抽牌堆第一張可疊加牌搜上手
      const want = deck.drawPile.find((c) => isStackableElement(CARDS[c.cardId]?.element ?? -1));
      deck.discardFromHand(uid);
      if (want) deck.tutor(want.cardId);
    }
    // 'cycle' → M3 不處理
  }
}

/**
 * 保守貪婪 AI：產出 team=aiTeam 的 3-tick SpellSchedule，並把出掉的牌
 * 從 deck.hand 移到 discardPile。filter 牌已於前置即時結算。
 *
 * 每 tick 取「分數最高且達門檻」的元素施放；疊加牌一次把手上同元素全疊上
 * （AI 既然判定此目標高收益，就集中傾瀉）；達不到門檻的 tick 直接 Pass。
 */
export function planAiSchedule(
  deck: Deck,
  units: BattleUnit[],
  _bases: Base[],
  terrain: TerrainGrid | null | undefined,
  aiTeam: 0 | 1,
): SpellSchedule {
  resolveAiFilters(deck);

  const schedule: SpellSchedule = [passCmd(0), passCmd(1), passCmd(2)];

  for (let tick = 0; tick < 3; tick++) {
    // 當前手牌的可施放元素（stackable + effect）
    const elements = new Set<ElementId>();
    for (const c of deck.hand) {
      const el = CARDS[c.cardId]?.element;
      if (el !== undefined) elements.add(el);
    }
    if (elements.size === 0) break;

    let best: Candidate | null = null;
    for (const el of elements) {
      const cand = evaluateElement(el, units, terrain, aiTeam);
      if (cand && (!best || cand.score > best.score)) best = cand;
    }
    if (!best) continue; // 保守：此 tick 無高收益 → 留牌

    // 消耗手牌：疊加牌把同元素全部疊上；效果牌出 1 張。
    const sameEl = deck.hand.filter((c) => CARDS[c.cardId]?.element === best!.element);
    const copies = isStackableElement(best.element) ? sameEl.length : 1;
    for (let i = 0; i < copies; i++) deck.discardFromHand(sameEl[i].uid);

    schedule[tick] = buildCast(tick, best.element, best.target, copies);
  }

  return schedule;
}
